import { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { SurveyDetailsItem } from '@core/api/useSurveys';
import { iOption } from '@core/models';
import { isDateInRange } from '@core/utils/date';

const alphabeticalSorter = (a: string, b: string) => a.localeCompare(b);
const optionMapper = (option: string) => ({
    id: option,
    title: option
});

export interface FilterCriteria {
    startDate: Dayjs;
    endDate: Dayjs;
    conceptTypes: string[];
    chainOrSegments: string[];
    itemTypes: string[];
    segments: string[];
    searchValue: string;
    enableDateRange: boolean;
}

export interface FilterOptions {
    conceptTypeOptions: iOption[];
    chainOrSegmentsOptions: iOption[];
    itemTypesOptions: iOption[];
    segmentOptions: iOption[];
}

export interface UseFilterSurveyItemsProps {
    items: SurveyDetailsItem[];
    filterCriteria: FilterCriteria;
    mappedSegments: Record<number, string>;
}

export const useFilterSurveyItems = ({
    items,
    filterCriteria,
    mappedSegments
}: UseFilterSurveyItemsProps) => {
    const {
        startDate,
        endDate,
        conceptTypes,
        chainOrSegments,
        itemTypes,
        segments,
        searchValue,
        enableDateRange,
    } = filterCriteria;

    // Filter items based on all criteria (exact logic from useSurveyAddItems)
    const filteredItems = useMemo((): SurveyDetailsItem[] => {
        const trimmedSearchValue = searchValue?.trim() ?? "";
        return items.filter((item) => {
            // Date range filtering
            const matchesDateRange = !enableDateRange || isDateInRange(item.date, startDate, endDate);
            
            // Concept type filtering
            const matchesConceptType = !conceptTypes.length || (item.testType && conceptTypes.includes(item.testType));
            
            // Chain/segment filtering
            const matchesChainOrSegment = !chainOrSegments.length || (item.testedFor && chainOrSegments.includes(item.testedFor));
            
            // Item type filtering
            const matchesItemType = !itemTypes.length || (item.itemType && itemTypes.includes(item.itemType));
            
            // Segment filtering
            const segmentId = item.segmentId ?? 0;
            const matchesSegment = !segments.length || (segmentId && segments.includes(mappedSegments[segmentId]));
            
            // Search filtering (by item name)
            const matchesSearch = trimmedSearchValue.length < 3 || (item.itemName?.toLowerCase()?.includes(trimmedSearchValue.toLowerCase()));

            return matchesDateRange && matchesConceptType && matchesChainOrSegment && matchesItemType && matchesSegment && matchesSearch;
        });
    }, [items, enableDateRange, startDate, endDate, conceptTypes, chainOrSegments, itemTypes, segments, searchValue, mappedSegments]);

    // Generate filter options (exact logic from useSurveyAddItems)
    const filterOptions = useMemo((): FilterOptions => {
        const conceptTypesSet = new Set<string>();
        const chainOrSegmentsSet = new Set<string>();
        const itemTypesSet = new Set<string>();
        const segmentsSet = new Set<string>();
        
        items.forEach(item => {
            const segmentId = item.segmentId ?? 0;
            if (item.testType) conceptTypesSet.add(item.testType);
            if (item.testedFor) chainOrSegmentsSet.add(item.testedFor);
            if (item.itemType) itemTypesSet.add(item.itemType);
            if (mappedSegments[segmentId]) segmentsSet.add(mappedSegments[segmentId]);
        });

        return {
            conceptTypeOptions: Array.from(conceptTypesSet).sort(alphabeticalSorter).map(optionMapper),
            chainOrSegmentsOptions: Array.from(chainOrSegmentsSet).sort(alphabeticalSorter).map(optionMapper),
            itemTypesOptions: Array.from(itemTypesSet).sort(alphabeticalSorter).map(optionMapper),
            segmentOptions: Array.from(segmentsSet).sort(alphabeticalSorter).map(optionMapper),
        };
    }, [items, mappedSegments]);

    return {
        filteredItems,
        filterOptions
    };
};
